import { cn } from '@/lib/utils'

type Props = {
  total: number
  completed: number
  size?: number
  className?: string
}

export const ProgressCircle = ({
  total,
  completed,
  size = 20,
  className,
}: Props) => {
  if (total === 0) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        className={className}
        aria-hidden="true"
      >
        <circle
          cx="10"
          cy="10"
          r="8"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-muted-foreground/40"
        />
      </svg>
    )
  }

  const radius = 8
  const cx = 10
  const cy = 10
  const gapAngleDeg = total > 1 ? 4 : 0
  const totalGap = total > 1 ? gapAngleDeg * total : 0
  const arcSpanDeg = (360 - totalGap) / total

  const toRad = (deg: number) => (deg * Math.PI) / 180

  const polarToCartesian = (angleDeg: number): { x: number; y: number } => {
    const rad = toRad(angleDeg - 90)
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    }
  }

  const describeArc = (startDeg: number, endDeg: number): string => {
    const start = polarToCartesian(startDeg)
    const end = polarToCartesian(endDeg)
    const largeArc = endDeg - startDeg > 180 ? 1 : 0
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`
  }

  const segments = Array.from({ length: total }, (_, i) => {
    const startDeg = i * (arcSpanDeg + gapAngleDeg)
    const endDeg = startDeg + arcSpanDeg
    const isDone = i < completed
    return { startDeg, endDeg, isDone }
  })

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      className={cn('shrink-0', className)}
      aria-label={`${completed} of ${total} steps completed`}
      role="img"
    >
      {segments.map((seg, i) => (
        <path
          key={i}
          d={describeArc(seg.startDeg, seg.endDeg)}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          className={
            seg.isDone ? 'stroke-success' : 'stroke-muted-foreground/40'
          }
        />
      ))}
    </svg>
  )
}
